import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

import type { BrowserAnalysis } from "./browser-analysis"
import type { MeasurementResult } from "./types"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatMetric(value: number, decimals = 2): string {
  return value.toFixed(decimals)
}

export function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

export function getMetricInterpretation(
  metric: string,
  value: number
): { label: string; color: string } {
  const interpretations: Record<
    string,
    { ranges: [number, string, string][] }
  > = {
    // JSD² scale (bounded [0,1]) — provisional, calibrate after 10-20 real analyses
    learning_rate: {
      ranges: [
        [0.005, "Stuck", "text-rose-400"],
        [0.03, "Steady", "text-emerald-400"],
        [0.10, "In Flow", "text-cyan-400"],
        [0.25, "Rapid Exploration", "text-amber-400"],
        [Infinity, "High Velocity", "text-orange-400"],
      ],
    },
    coherence: {
      ranges: [
        [0.3, "Scattered", "text-rose-400"],
        [0.6, "Connecting", "text-amber-400"],
        [0.8, "Integrated", "text-cyan-400"],
        [Infinity, "Deeply Integrated", "text-emerald-400"],
      ],
    },
    gradient_signal: {
      ranges: [
        [0.2, "Searching", "text-muted"],
        [0.5, "Building", "text-cyan-400"],
        [0.8, "Focused", "text-emerald-400"],
        [Infinity, "Locked In", "text-indigo-400"],
      ],
    },
  }

  const config = interpretations[metric]
  if (!config) return { label: "", color: "text-muted" }

  for (const [threshold, label, color] of config.ranges) {
    if (value < threshold) return { label, color }
  }
  return { label: "", color: "text-muted" }
}

// ─── Identity Label Classification (16-archetype taxonomy) ──────────────────
//
// 6 archetype families based on 8 metrics:
//   1. Depth-oriented    (low topic diversity + high engagement depth)
//   2. Breadth-oriented  (high topic diversity + high entropy)
//   3. Interrogative     (high question ratio)
//   4. Elaborative       (high avg message length + rich vocabulary)
//   5. Sustained         (high session count + temporal spread)
//   6. Conversational    (high engagement depth + session count)

const ARCHETYPE_DESCRIPTORS: Record<string, string> = {
  "The Diver":
    "Goes to the bottom of everything. Where others skim the surface, you're already three levels down.",
  "The Perfectionist":
    "Won't ship it until every detail is right. Rich vocabulary, minimal questions, maximum clarity.",
  "The Monk":
    "Returns to the same ground, month after month. Depth through devotion, not novelty.",
  "The Polymath":
    "Interested in everything. Good at more of it than you should be.",
  "The Connector":
    "Sees the thread between things that don't seem related. Breadth in service of synthesis.",
  "The Wanderer":
    "Follows whatever's interesting. Ends up somewhere surprising.",
  "The Archaeologist":
    "Digs for what's buried. Keeps going until you find it.",
  "The Sparring Partner":
    "Thinks by bouncing off someone else. The conversation IS the thinking.",
  "The Questioner":
    "Asks the question nobody else thought to ask. Then asks it again differently.",
  "The Systems Thinker":
    "Sees how everything connects. Builds the model before building the thing.",
  "The Storyteller":
    "Makes sense of things by telling the story of them.",
  "The Theorist":
    "Needs a framework before they can act. Builds theories the way others build furniture.",
  "The Student":
    "Always learning. Treats everything as curriculum.",
  "The Refiner":
    "First draft is never the point. The polishing IS the thinking.",
  "The Co-Pilot":
    "Thinks best alongside someone else. The partnership makes both better.",
  "The Bridge Builder":
    "Connects worlds that don't normally talk to each other.",
}

const ARCHETYPE_INSIGHTS: Record<string, string> = {
  "The Diver":
    "You don't skim — you bore straight to the core. Your conversations reveal someone who won't move on until they've truly understood.",
  "The Perfectionist":
    "You treat every exchange like it matters. Your precision isn't rigidity — it's care disguised as craft.",
  "The Monk":
    "You return to the same territory again and again, and each time you see something new. That's not repetition — it's depth.",
  "The Polymath":
    "Your mind doesn't stay in one place. It jumps between worlds, and the surprising part is how much you retain from each visit.",
  "The Connector":
    "You see bridges where others see walls. Your thinking naturally links disparate ideas into something coherent.",
  "The Wanderer":
    "You follow curiosity wherever it leads. What looks like wandering is actually a highly personal form of exploration.",
  "The Archaeologist":
    "You keep digging until you find what's underneath. Your questions aren't idle — they're excavations.",
  "The Sparring Partner":
    "You think by pushing back. The friction isn't conflict — it's how you sharpen your understanding.",
  "The Questioner":
    "You ask the questions others skip. Your curiosity is relentless, and it pulls conversations into deeper territory.",
  "The Systems Thinker":
    "You see the machine behind the surface. Before you build anything, you need to understand how all the pieces fit together.",
  "The Storyteller":
    "You make sense of complexity by finding the narrative thread. Your thinking unfolds like a story being told.",
  "The Theorist":
    "You need the framework before the action. Your mind builds models first — and the models are often better than the thing itself.",
  "The Student":
    "You approach every conversation as a chance to learn. That openness is rarer than you think.",
  "The Refiner":
    "Your first pass is never the point. You polish, adjust, reconsider — and the result is sharper every time.",
  "The Co-Pilot":
    "You think best in partnership. Your conversations aren't solo performances — they're duets.",
  "The Bridge Builder":
    "You translate between worlds that don't normally speak to each other. That's a rare and valuable instinct.",
}

export function classifyThinkingStyleBrowser(
  analysis: BrowserAnalysis
): { label: string; descriptor: string; insight: string } {
  const topics = analysis.topicDiversity
  const qRatio = analysis.explorationRatio / 10 // back to 0–1
  const msgLen = analysis.avgMessageLength
  const sessions = analysis.sessionCount
  const days = analysis.durationDays ?? 0
  const vocab = analysis.informationDensity // 1–10 scale
  const depth = analysis.engagementDepth
  const entropy = analysis.thinkingComplexity // 0–10 scale

  let label: string

  // 1. Depth-oriented (narrow focus + high engagement depth)
  if (topics <= 3 && depth > 15) {
    if (vocab > 7.5 && qRatio < 0.15) {
      label = "The Perfectionist"
    } else if (entropy < 4 && days > 30) {
      label = "The Monk"
    } else {
      label = "The Diver"
    }
  }
  // 2. Breadth-oriented (high topic diversity + high entropy)
  else if (topics >= 6 && entropy > 5) {
    if (vocab > 8 && entropy > 7) {
      label = "The Polymath"
    } else if (depth > 12) {
      label = "The Connector"
    } else if (qRatio > 0.30) {
      label = "The Wanderer"
    } else {
      label = "The Connector"
    }
  }
  // 3. Interrogative (high question ratio)
  else if (qRatio > 0.30) {
    if (qRatio > 0.40 && topics >= 5) {
      label = "The Questioner"
    } else if (sessions > 50) {
      label = "The Sparring Partner"
    } else {
      label = "The Archaeologist"
    }
  }
  // 4. Elaborative (long messages + rich vocabulary)
  else if (msgLen > 150) {
    if (vocab > 7.5 && qRatio < 0.20) {
      label = "The Systems Thinker"
    } else if (msgLen > 220 && days > 45) {
      label = "The Storyteller"
    } else {
      label = "The Theorist"
    }
  }
  // 5. Sustained engagement (high session count + temporal spread)
  else if (sessions > 50 && days > 60) {
    if (sessions > 80 && days > 90) {
      label = "The Refiner"
    } else {
      label = "The Student"
    }
  }
  // 6. Conversational (high engagement depth)
  else if (depth > 16) {
    if (topics >= 5 && vocab > 8) {
      label = "The Bridge Builder"
    } else {
      label = "The Co-Pilot"
    }
  }
  // 7. Fallback
  else {
    if (topics > 4) {
      label = "The Wanderer"
    } else {
      label = "The Diver"
    }
  }

  const desc = ARCHETYPE_DESCRIPTORS[label] ?? ""
  const insight = ARCHETYPE_INSIGHTS[label] ?? "Your conversations reveal a distinct thinking pattern — one that's worth understanding more deeply."

  return {
    label,
    descriptor: `${desc} Based on ${analysis.totalTurns.toLocaleString()} conversation turns.`,
    insight,
  }
}

/**
 * Truncate narrative text to a given number of sentences, splitting on sentence boundaries.
 */
export function truncateNarrative(text: string | undefined, maxSentences: number): string {
  if (!text) return ""
  // Split on sentence-ending punctuation followed by whitespace
  const sentences = text.match(/[^.!?]+[.!?]+[\s]*/g)
  if (!sentences) return text
  return sentences.slice(0, maxSentences).join("").trim()
}

const PAID_LABEL_MATRIX: Record<string, Record<string, string>> = {
  learning: {
    coherence: "The Sponge",
    gradient: "The Apprentice",
    efficiency: "The Quick Study",
    breadth: "The Renaissance Mind",
  },
  coherence: {
    learning: "The Weaver",
    gradient: "The Compass",
    efficiency: "The Architect",
    breadth: "The Cartographer",
  },
  gradient: {
    learning: "The Climber",
    coherence: "The Navigator",
    efficiency: "The Sprinter",
    breadth: "The Prospector",
  },
  efficiency: {
    learning: "The Surgeon",
    coherence: "The Watchmaker",
    gradient: "The Minimalist",
    breadth: "The Scout",
  },
  breadth: {
    learning: "The Collector",
    coherence: "The Translator",
    gradient: "The Pioneer",
    breadth: "The Strategist",
  },
}

export function classifyThinkingStyle(
  result: MeasurementResult
): { label: string; descriptor: string } {
  // Special cases first
  if ((result.phase_transition_risk ?? 0) > 0.3 && result.gradient_signal > 0.4) {
    return {
      label: "Phase Shifter",
      descriptor: `Your patterns show signs of an approaching cognitive transition — your thinking is reorganizing. Across ${result.territories.length} territories, something is about to change.`,
    }
  }

  const metrics: [string, number][] = [
    ["learning", Math.min(1, result.learning_rate / 0.10)],
    ["coherence", result.coherence],
    ["gradient", result.gradient_signal],
    ["efficiency", result.path_efficiency],
    ["breadth", Math.min(1, result.territories.length / 8)],
  ]

  metrics.sort((a, b) => b[1] - a[1])
  const [primaryAxis, primaryScore] = metrics[0]
  const [secondaryAxis] = metrics[1]

  if (primaryScore < 0.3) {
    return {
      label: "Quiet Thinker",
      descriptor: "Your profile is balanced — no single dimension dominates. You engage evenly across all modes of thinking.",
    }
  }

  const label = PAID_LABEL_MATRIX[primaryAxis]?.[secondaryAxis] || "Independent Thinker"

  const PAID_DESCRIPTORS: Record<string, string> = {
    // learning × ...
    "The Sponge": "You absorb fast and connect everything. New information doesn't sit in isolation — it gets woven into what you already know.",
    "The Apprentice": "You learn with purpose. Each conversation moves you closer to something specific, and you're accelerating.",
    "The Quick Study": "You extract signal from noise fast. When something works, you lock it in and move on.",
    "The Renaissance Mind": "You range wide and make it all cohere. Most people explore OR integrate — you do both.",
    // coherence × ...
    "The Weaver": "Your ideas talk to each other. What you learn in one domain shows up in another.",
    "The Compass": "Your mind has a center of gravity. Everything connects back to a core purpose.",
    "The Architect": "You build bridges between ideas automatically. Where others see separate domains, you see one system.",
    "The Cartographer": "You see the whole picture. Your thinking naturally integrates across many domains into coherent frameworks.",
    // gradient × ...
    "The Climber": "You know where you're going and you're getting there fast. Clear direction, strong momentum.",
    "The Navigator": "You move with both direction and depth. Your trajectory is deliberate and your connections are real.",
    "The Sprinter": "Efficient and focused. You build in straight lines — minimum waste, maximum output.",
    "The Prospector": "You range wide but always converge. Your breadth serves a focused purpose.",
    // efficiency × ...
    "The Surgeon": "You learn with surgical focus. High efficiency, low waste, each conversation builds exactly on the last.",
    "The Watchmaker": "You integrate systematically. Nothing is random — each connection is deliberate.",
    "The Minimalist": "Your thinking is lean. You cut straight to what matters and build from there.",
    "The Scout": "You cover ground fast and don't waste time on dead ends. Broad but selective.",
    // breadth × ...
    "The Collector": "You learn everything. Your breadth is your weapon — you bring insights from domains others don't even know exist.",
    "The Translator": "You live between disciplines. Your best ideas come from connecting fields that don't usually talk.",
    "The Pioneer": "You go where others don't. Wide exploration with a clear forward direction.",
    "The Strategist": "You see more of the board than most. Breadth plus efficiency means nothing surprises you.",
    // special
    "Phase Shifter": "Your thinking is reorganizing. The patterns show a transition approaching — something is about to change.",
  }

  const topTerritory = result.territories
    .slice()
    .sort((a, b) => b.energy - a.energy)[0]

  const behavioralDesc = PAID_DESCRIPTORS[label] || ""
  const statsContext = topTerritory
    ? ` Most of your attention goes to ${topTerritory.label}.`
    : ""

  // Map axis names to human-readable metric descriptions
  const AXIS_NAMES: Record<string, string> = {
    learning: "learning speed",
    coherence: "integration",
    gradient: "direction",
    efficiency: "path efficiency",
    breadth: "breadth",
  }
  const basis = ` Your strongest signal is ${AXIS_NAMES[primaryAxis] || primaryAxis}, followed by ${AXIS_NAMES[secondaryAxis] || secondaryAxis}.`

  return {
    label,
    descriptor: behavioralDesc + statsContext + basis,
  }
}
